import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ResendResponse,
  SupportContact,
  UseOtpResendOptions,
  UseOtpResendResult,
} from 'src/types/otpResend';
import { messageForContactError } from 'src/utils/errors/contactErrorMessages';

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: Record<string, unknown> & {
      error_code?: string;
      detail?: string;
      cooldown_seconds_remaining?: number;
      resend_count?: number;
      resend_count_max?: number;
      support_contact?: SupportContact | null;
      exhausted?: boolean;
    };
  };
  status?: number;
  // The shared axios response interceptor flattens `response.data` onto the
  // rejected error, so the server's error_code/detail may sit at the top level.
  error_code?: string;
  detail?: string;
  cooldown_seconds_remaining?: number;
  resend_count?: number;
  resend_count_max?: number;
  support_contact?: SupportContact | null;
  exhausted?: boolean;
}

function extractResendData(
  source: Record<string, unknown> | null | undefined
): ResendResponse {
  if (!source) {
    return {
      next_allowed_at: null,
      cooldown_seconds_remaining: 0,
      resend_count: 0,
      resend_count_max: 3,
      support_contact: null,
      exhausted: false,
    };
  }
  return {
    next_allowed_at: (source.next_allowed_at as string) ?? null,
    cooldown_seconds_remaining:
      (source.cooldown_seconds_remaining as number) ?? 0,
    resend_count: (source.resend_count as number) ?? 0,
    resend_count_max: (source.resend_count_max as number) ?? 3,
    support_contact: (source.support_contact as SupportContact) ?? null,
    exhausted: (source.exhausted as boolean) ?? false,
  };
}

function extractResendFromError(err: AxiosLikeError): ResendResponse | null {
  if (!err) {
    return null;
  }
  const data = err.response?.data ?? {};
  const direct = {
    cooldown_seconds_remaining:
      err.cooldown_seconds_remaining ?? data.cooldown_seconds_remaining,
    resend_count: err.resend_count ?? data.resend_count,
    resend_count_max: err.resend_count_max ?? data.resend_count_max,
    support_contact: err.support_contact ?? data.support_contact,
    exhausted: err.exhausted ?? data.exhausted,
  };
  if (
    direct.cooldown_seconds_remaining != null ||
    direct.resend_count != null ||
    direct.resend_count_max != null ||
    direct.exhausted != null
  ) {
    return extractResendData(direct as Record<string, unknown>);
  }
  return null;
}

export function useOtpResend({
  sendFn,
  onSuccess,
  defaultCooldownSeconds = 0,
  initialCooldownSeconds = 0,
}: UseOtpResendOptions): UseOtpResendResult {
  const [remainingSeconds, setRemainingSeconds] = useState(
    initialCooldownSeconds
  );
  const [attempt, setAttempt] = useState(0);
  const [attemptMax, setAttemptMax] = useState(3);
  const [isExhausted, setIsExhausted] = useState(false);
  const [supportContact, setSupportContact] = useState<SupportContact | null>(
    null
  );
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const canResend = !isSending && !isExhausted && remainingSeconds <= 0;

  useEffect(() => {
    if (remainingSeconds <= 0) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = setInterval(() => {
      setRemainingSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [remainingSeconds]);

  const send = useCallback(async (): Promise<void> => {
    if (!canResend) {
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      const result = await sendFn();
      const data = (result || {}) as Record<string, unknown>;
      const parsed = extractResendData(data);
      const cooldown =
        parsed.cooldown_seconds_remaining || defaultCooldownSeconds;
      setRemainingSeconds(cooldown);
      setAttempt(parsed.resend_count || 1);
      setAttemptMax(parsed.resend_count_max || 3);
      setIsExhausted(parsed.exhausted);
      setSupportContact(parsed.support_contact);
      if (!parsed.exhausted && onSuccess) {
        onSuccess(parsed);
      }
    } catch (err: unknown) {
      const axiosErr = err as AxiosLikeError;

      const resendFromError = extractResendFromError(axiosErr);
      if (resendFromError) {
        const cooldown =
          resendFromError.cooldown_seconds_remaining || defaultCooldownSeconds;
        setRemainingSeconds(cooldown);
        setAttempt(resendFromError.resend_count || 1);
        setAttemptMax(resendFromError.resend_count_max || 3);
        setIsExhausted(resendFromError.exhausted);
        setSupportContact(resendFromError.support_contact);
        if (!resendFromError.exhausted && onSuccess) {
          onSuccess(resendFromError);
        }
        return;
      }

      // Surface the server's safe, actionable message (e.g. the 409
      // contact_already_claimed from the pending-verification resend) instead
      // of masking every non-cooldown failure as a generic retry.
      const rawDetail = axiosErr.detail ?? axiosErr.response?.data?.detail;
      const errorCode =
        axiosErr.error_code ?? axiosErr.response?.data?.error_code;
      setError(
        errorCode || typeof rawDetail === 'string'
          ? messageForContactError({
              error_code: errorCode ?? null,
              detail: typeof rawDetail === 'string' ? rawDetail : null,
            })
          : 'Failed to send code. Please try again.'
      );
    } finally {
      setIsSending(false);
    }
  }, [canResend, sendFn, onSuccess, defaultCooldownSeconds]);

  const seed = useCallback((response: ResendResponse): void => {
    const parsed = extractResendData(
      response as unknown as Record<string, unknown>
    );
    setRemainingSeconds(parsed.cooldown_seconds_remaining || 0);
    setAttempt(parsed.resend_count || 0);
    setAttemptMax(parsed.resend_count_max || 3);
    setIsExhausted(parsed.exhausted);
    setSupportContact(parsed.support_contact);
  }, []);

  return {
    send,
    seed,
    remainingSeconds,
    canResend,
    attempt,
    attemptMax,
    isExhausted,
    supportContact,
    isSending,
    error,
  };
}

export default useOtpResend;
